import * as React from 'react';
import { useUserCanPerformAction } from '@/utils/hooks/useUserCanPerformAction';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import Avatar from 'antd/lib/avatar';
import Button from 'antd/lib/button';
import Checkbox from 'antd/lib/checkbox';
import Dropdown from 'antd/lib/dropdown';
import Input from 'antd/lib/input';
import Select from 'antd/lib/select';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import {
    BugOutlined,
    BulbFilled,
    CaretDownOutlined,
    CheckCircleOutlined,
    ClockCircleFilled,
    CloseCircleOutlined,
    EllipsisOutlined,
    ExperimentOutlined,
    FlagOutlined,
    LineChartOutlined,
    LinkOutlined,
    LoadingOutlined,
    MinusCircleOutlined,
    MonitorOutlined,
    PlusCircleOutlined,
    ReconciliationOutlined,
    SearchOutlined,
    SendOutlined,
    StarOutlined,
    TagOutlined,
    RobotOutlined,
    TeamOutlined,
    ToolFilled,
    ToolOutlined,
    UndoOutlined,
    ZoomInOutlined,
} from '@ant-design/icons';
import {
    antGold,
    antGrey,
    antMagenta,
    colorWithAlpha,
    getAvatarColorForName,
    runwayColors,
    rwColors,
} from '@/components/styles/globalStyles';
import { IntegrationType, IssueEntity, IssueSource, IssueSourceType, IssueState } from '@/types';
import { RootState } from '@/reducers/reducers';
import { selectReleasesForAppId, selectRequiresFixRequestsApprovals, selectUserById } from '@/selectors/selectors';
import IntegrationIcon from '@/components/icons/IntegrationIcon';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { DISALLOWED_ACTION_ERROR_MESSAGE } from '@/components/HOCs/PermissionedActionButton';
import { selectAppOrgUsers, selectSortedGroups } from '@/selectors/memoizedSelectors';
import {
    TriageViewType,
    TRY_REOPEN_ISSUE,
    TRY_TRANSITION_ISSUE,
    TRY_UPDATE_ISSUE,
    TRY_UPDATE_ISSUE_ASSIGNEES,
    tryReopenIssue,
    tryTransitionIssue,
    tryUpdateIssue,
} from '@/actions/triage';
import { displayTextForUserChecklistGroup, displayUserNameLong } from '@/helpers/displayHelpers';
import { useIsLoading, useIsRequiredFixApprover } from '@/utils/hooks/helpers';
import { RequestFixModal, requestFixActionLabel } from './RequestFixModal';
import { useHistory } from 'react-router-dom';
import { AddSourceModal } from './AddSourceModal';
import { UnmergeSourceModal } from './UnmergeSourceModal';
import sparkleIcon from '../../../assets/sparkle.svg';
import MarkdownComponent from '@/components/HOCs/Markdown';
import {
    CLOSED_ISSUE_STATES,
    DESCRIPTION_MARKDOWN_COMPONENTS,
    IssueSourceIcon,
    sortUsersBySuggestedFirst,
    AutoAssignmentReasons,
    hasAutoAssignmentReasons,
    ASSIGNEE_AGENT_PREFIX,
    ASSIGNEE_GROUP_PREFIX,
    ASSIGNEE_USER_PREFIX,
    agentDisplayName,
    currentAssigneeKey,
    useAssignIssue,
    useIssueAgents,
} from './triageHelpers';
import { MarkRelatedModal } from './MarkRelatedModal';
import { cardGridColumns, FixRequestModule, LABEL_STYLE, UserAvatarWithTooltip } from './FixRequestModule';

const stopCardClick = (e: React.MouseEvent) => e.stopPropagation();

// Shared by the clamped preview and the expanded overlay so the text doesn't shift when it expands.
const DESCRIPTION_MARKDOWN_STYLE: React.CSSProperties = {
    color: antGrey.grey7,
    fontSize: 12,
    lineHeight: '18px',
};

export const SCROLL_TO_RELATED_ISSUES = 'relatedIssues';

export const SOURCE_TYPE_ICON: Record<IssueSourceType, React.ReactNode> = {
    [IssueSourceType.Runway]: <TeamOutlined />,
    [IssueSourceType.PMTicket]: <TagOutlined />,
    [IssueSourceType.UserReviews]: <StarOutlined />,
    [IssueSourceType.BetaFeedback]: <ExperimentOutlined />,
    [IssueSourceType.RegressionTesting]: <ReconciliationOutlined />,
    [IssueSourceType.StabMon]: <BugOutlined />,
    [IssueSourceType.OBAA]: <LineChartOutlined />,
};

const SOURCE_TYPE_LABEL: Record<IssueSourceType, string> = {
    [IssueSourceType.Runway]: 'Team',
    [IssueSourceType.PMTicket]: 'Ticket',
    [IssueSourceType.UserReviews]: 'User reviews',
    [IssueSourceType.BetaFeedback]: 'Beta feedback',
    [IssueSourceType.RegressionTesting]: 'Regression testing',
    [IssueSourceType.StabMon]: 'Stability monitoring',
    [IssueSourceType.OBAA]: 'Observability & analytics',
};

// Health metric sources name the rollout step they were detected on when a release has several.
const sourceTypeLabel = (source: IssueSource): string => {
    const label = SOURCE_TYPE_LABEL[source.type];
    const stepName = source.healthMetrics?.stepName;
    return stepName ? `${label} | ${stepName}` : label;
};

// Beyond one source, the chip stands for several: name each kind, with a count when it repeats.
const sourcesTooltip = (sources: IssueSource[]): React.ReactNode => {
    if (sources.length === 1) {
        return sourceTypeLabel(sources[0]);
    }
    const countByLabel = new Map<string, number>();
    sources.forEach((source) => {
        const label = sourceTypeLabel(source);
        countByLabel.set(label, (countByLabel.get(label) ?? 0) + 1);
    });
    return (
        <div>
            <div>{`${sources.length} associated issues`}</div>
            {[...countByLabel].map(([label, count]) => (
                <div key={label}>{count > 1 ? `- ${label} (${count})` : `- ${label}`}</div>
            ))}
        </div>
    );
};

interface StateMeta {
    label: string;
    icon: React.ReactNode;
}

export const stateMeta = (state: IssueState, requiresFixApprovals = true): StateMeta => {
    if (state === IssueState.FixRequested && !requiresFixApprovals) {
        return {
            label: 'Fix in progress',
            icon: <ClockCircleFilled style={{ color: rwColors.statusYellow, fontSize: 16 }} />,
        };
    }
    switch (state) {
        case IssueState.New:
            return { label: 'Needs triage', icon: <SendOutlined style={{ color: rwColors.primary, fontSize: 16 }} /> };
        case IssueState.Assigned:
            return {
                label: 'Assigned',
                icon: <MonitorOutlined style={{ color: rwColors.statusYellow, fontSize: 16 }} />,
            };
        case IssueState.FixRequested:
            return {
                label: 'Fix requested',
                icon: <ToolFilled style={{ color: rwColors.statusYellow, fontSize: 16 }} />,
            };
        case IssueState.FixApproved:
            return {
                label: 'Fix pending',
                icon: <ClockCircleFilled style={{ color: rwColors.statusYellow, fontSize: 16 }} />,
            };
        case IssueState.Reopened:
            return {
                label: 'Reopened',
                icon: <UndoOutlined style={{ color: rwColors.statusYellow, fontSize: 16 }} />,
            };
        case IssueState.FixMerged:
            return {
                label: 'Resolved',
                icon: <CheckCircleOutlined style={{ color: rwColors.statusGreen, fontSize: 16 }} />,
            };
        case IssueState.ResolvedExternally:
            return {
                label: 'Resolved externally',
                icon: <CheckCircleOutlined style={{ color: rwColors.statusGreen, fontSize: 16 }} />,
            };
        case IssueState.FixRejected:
            return {
                label: 'Unresolved',
                icon: <CloseCircleOutlined style={{ color: antGrey.grey6, fontSize: 16 }} />,
            };
        case IssueState.NotAPriority:
        default:
            return {
                label: 'Not a priority',
                icon: <CloseCircleOutlined style={{ color: antGrey.grey6, fontSize: 16 }} />,
            };
    }
};

export const TriageIssueCard: React.FC<{
    issue: IssueEntity;
    orgId: string;
    preview?: boolean;
    disabled?: boolean;
    viewType: TriageViewType;
    basePath?: string;
    selectMode?: boolean;
    selected?: boolean;
    selectDisabled?: boolean;
    onToggleSelected?: () => void;
}> = ({
    issue,
    orgId,
    preview,
    disabled,
    viewType,
    basePath,
    selectMode,
    selected,
    selectDisabled,
    onToggleSelected,
}) => {
    const history = useHistory();
    const showFixModule = !!issue.fixRequest || !!issue.targetVersion;
    // Dim the whole card for deprioritized / rejected issues to signal they won't be acted on.
    const isDimmed = issue.state === IssueState.FixRejected || issue.state === IssueState.NotAPriority;
    const openDrawer = () => {
        if (!preview && basePath) history.push(`${basePath}/${issue.id}`);
    };
    const card = (
        <div
            onClick={preview ? undefined : openDrawer}
            style={{
                border: `1px solid ${antGrey.grey5}`,
                borderRadius: 8,
                overflow: 'hidden',
                backgroundColor: antGrey.grey2,
                cursor: preview ? undefined : 'pointer',
                ...(selectMode ? { flex: 1, minWidth: 0 } : {}),
            }}
        >
            {/* Dim closed/deprioritized issues per-section so the reopen / overflow actions stay fully visible. */}
            {showFixModule && !preview && (
                <FixRequestModule
                    issue={issue}
                    orgId={orgId}
                    dimmed={isDimmed}
                    disabled={disabled}
                    viewType={viewType}
                />
            )}
            <IssueBody
                issue={issue}
                orgId={orgId}
                preview={preview}
                disabled={disabled}
                viewType={viewType}
                dimmed={isDimmed}
                basePath={basePath}
            />
        </div>
    );
    if (!selectMode) {
        return card;
    }
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Checkbox checked={!!selected} disabled={selectDisabled} onChange={() => onToggleSelected?.()} />
            {card}
        </div>
    );
};
const IssueBody: React.FC<{
    issue: IssueEntity;
    orgId: string;
    preview?: boolean;
    disabled?: boolean;
    viewType: TriageViewType;
    dimmed?: boolean;
    basePath?: string;
}> = ({ issue, orgId, preview, disabled, viewType, dimmed, basePath }) => {
    const hasReporter = !!issue.reporter;
    // Cells share cardGridColumns so the fix-request bar aligns with these columns. Each cell owns its own
    // background + padding (grid stretches them to full row height — no negative-margin bleed needed).
    // Everything dims for closed issues except the actions column, which stays fully visible.
    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: cardGridColumns(hasReporter, preview),
                backgroundColor: antGrey.grey2,
            }}
        >
            <StatusColumn issue={issue} dimmed={dimmed} basePath={preview ? undefined : basePath} />
            <ContentColumn issue={issue} dimmed={dimmed} />
            {hasReporter && !preview && <ReporterSection issue={issue} orgId={orgId} dimmed={dimmed} />}
            <AssigneeSection
                issue={issue}
                orgId={orgId}
                preview={preview}
                disabled={disabled}
                viewType={viewType}
                dimmed={dimmed}
                basePath={basePath}
            />
            {!preview && <VersionColumn issue={issue} orgId={orgId} dimmed={dimmed} disabled={disabled} />}
            {!preview && <ActionsColumn issue={issue} orgId={orgId} viewType={viewType} disabled={disabled} />}
        </div>
    );
};

const StatusColumn: React.FC<{ issue: IssueEntity; dimmed?: boolean; basePath?: string }> = ({
    issue,
    dimmed,
    basePath,
}) => {
    const history = useHistory();
    const requiresFixApprovals = useSelector(selectRequiresFixRequestsApprovals(issue.appId));
    const meta = stateMeta(issue.state, requiresFixApprovals);
    const sources = issue.sources ?? [];
    const distinctSourceTypes = Array.from(new Set(sources.map((s) => s.type)));
    const hasPotentialCause = !!issue.potentialCauses?.causes?.length;
    const hasRelatedIssues = !!issue.relatedIssueIds?.length;
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                padding: '20px 16px 16px',
                backgroundColor: antGrey.grey2,
                opacity: dimmed ? 0.6 : undefined,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {meta.icon}
                <Text style={LABEL_STYLE}>{meta.label}</Text>
                {hasPotentialCause && (
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginLeft: 'auto',
                            padding: 3,
                            borderRadius: 2,
                            backgroundColor: antGold.gold5,
                        }}
                    >
                        <BulbFilled style={{ color: runwayColors.white, fontSize: 14 }} />
                    </span>
                )}
            </div>
            {(hasRelatedIssues || sources.length > 0) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 'auto' }}>
                    {hasRelatedIssues && (
                        <Tooltip
                            placement="top"
                            title={`${issue.relatedIssueIds.length} related issue${issue.relatedIssueIds.length > 1 ? 's' : ''}`}
                        >
                            <div
                                onClick={
                                    basePath ?
                                        (e) => {
                                            e.stopPropagation();
                                            history.push(
                                                `${basePath}/${issue.id}?scrollTo=${SCROLL_TO_RELATED_ISSUES}`,
                                            );
                                        }
                                    :   undefined
                                }
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    boxSizing: 'border-box',
                                    height: 24,
                                    padding: '0 4px',
                                    border: `1px solid ${rwColors.primary}`,
                                    borderRadius: 4,
                                    backgroundColor: antGrey.grey1,
                                    color: rwColors.primary,
                                    fontSize: 14,
                                    lineHeight: 0,
                                    cursor: basePath ? 'pointer' : undefined,
                                }}
                            >
                                <LinkOutlined />
                            </div>
                        </Tooltip>
                    )}
                    {sources.length > 0 && (
                        <Tooltip placement="top" title={sourcesTooltip(sources)}>
                            <div
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    // Fixed height shared with the related-issues chip: the count text's
                                    // line-height would otherwise make this chip taller than its neighbor.
                                    boxSizing: 'border-box',
                                    height: 24,
                                    padding: '0 4px',
                                    border: `1px solid ${antGrey.grey6}`,
                                    borderRadius: 4,
                                    backgroundColor: antGrey.grey1,
                                    color: antGrey.grey6,
                                    fontSize: 14,
                                    lineHeight: 0,
                                }}
                            >
                                {sources.length > 1 && (
                                    <Text
                                        style={{ fontSize: 14, lineHeight: 1, color: antGrey.grey6, fontWeight: 600 }}
                                    >
                                        {sources.length}x
                                    </Text>
                                )}
                                {distinctSourceTypes.map((type) => (
                                    <span key={type} style={{ display: 'inline-flex' }}>
                                        {SOURCE_TYPE_ICON[type]}
                                    </span>
                                ))}
                            </div>
                        </Tooltip>
                    )}
                </div>
            )}
        </div>
    );
};

const TITLE_LINE_HEIGHT = 22;

const ContentColumn: React.FC<{ issue: IssueEntity; dimmed?: boolean }> = ({ issue, dimmed }) => {
    const source = issue.sources?.[0];
    const description = issue.description;
    const title = issue.title;
    const stepNames = Array.from(
        new Set((issue.sources ?? []).map((s) => s.healthMetrics?.stepName).filter((name): name is string => !!name)),
    );
    const sourceIcon = <IssueSourceIcon source={source} appId={issue.appId} />;
    const [descTruncated, setDescTruncated] = React.useState(false);
    // expandedRect = the content row (tracks whether the overlay is open); cellRect = the full white
    // content cell, which stretches to the module height (drives the overlay edges + the zoom icon in the corner).
    const [expandedRect, setExpandedRect] = React.useState<DOMRect | null>(null);
    const [cellRect, setCellRect] = React.useState<DOMRect | null>(null);
    const [overlayVisible, setOverlayVisible] = React.useState(false);
    const descRef = React.useRef<HTMLDivElement>(null);
    const cellRef = React.useRef<HTMLDivElement>(null);
    const descTextRef = React.useRef<HTMLDivElement>(null);
    const overlayRef = React.useRef<HTMLDivElement>(null);
    const openOverlay = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (descRef.current) setExpandedRect(descRef.current.getBoundingClientRect());
        if (cellRef.current) setCellRect(cellRef.current.getBoundingClientRect());
    };
    const closeOverlay = () => {
        // Fade out, then unmount once the transition finishes.
        setOverlayVisible(false);
        window.setTimeout(() => setExpandedRect(null), 150);
    };
    // Fade the overlay in on the frame after it mounts (opacity 0 → 1), mirroring the phased-release widget.
    React.useEffect(() => {
        if (!expandedRect) return undefined;
        const id = requestAnimationFrame(() => setOverlayVisible(true));
        return () => cancelAnimationFrame(id);
    }, [expandedRect]);
    // Detect truncation of the CSS-clamped description ourselves (antd's JS ellipsis measured unreliably).
    React.useLayoutEffect(() => {
        const el = descTextRef.current;
        if (!el) return undefined;
        const check = () => setDescTruncated(el.scrollHeight > el.clientHeight + 1);
        check();
        const ro = new ResizeObserver(check);
        ro.observe(el);
        return () => ro.disconnect();
    }, [description]);
    React.useEffect(() => {
        if (!expandedRect) return undefined;
        // Position is captured at open time, so page scroll/resize would desync it — hard-close immediately.
        const hardClose = () => {
            setOverlayVisible(false);
            setExpandedRect(null);
        };
        // But scrolling *inside* the overlay (long descriptions scroll internally) must not close it.
        const onScroll = (e: Event) => {
            if (overlayRef.current && overlayRef.current.contains(e.target as Node)) return;
            hardClose();
        };
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', hardClose);
        return () => {
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', hardClose);
        };
    }, [expandedRect]);
    return (
        <div
            ref={cellRef}
            style={{
                position: 'relative',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                padding: 16,
                backgroundColor: runwayColors.white,
                opacity: dimmed ? 0.6 : undefined,
            }}
        >
            <div ref={descRef} style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, minWidth: 0 }}>
                    <div
                        style={{
                            width: 16,
                            height: TITLE_LINE_HEIGHT,
                            flexShrink: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            lineHeight: 0,
                        }}
                    >
                        {sourceIcon}
                    </div>
                    {title && (
                        <Text
                            style={{
                                flex: 1,
                                minWidth: 0,
                                fontSize: 14,
                                lineHeight: `${TITLE_LINE_HEIGHT}px`,
                                fontWeight: 500,
                                color: antGrey.grey10,
                                wordBreak: 'break-word',
                            }}
                        >
                            {title}
                            {stepNames.length > 0 && (
                                <>
                                    <Text style={{ color: antGrey.grey5, margin: '0 8px' }}>|</Text>
                                    <Text style={{ fontWeight: 400, color: antGrey.grey8 }}>
                                        {stepNames.join(', ')}
                                    </Text>
                                </>
                            )}
                        </Text>
                    )}
                </div>
                {description && (
                    <div
                        ref={descTextRef}
                        style={{
                            marginLeft: 32,
                            color: antGrey.grey7,
                            fontSize: 12,
                            lineHeight: '18px',
                            wordBreak: 'break-word',
                            maxHeight: 54,
                            overflow: 'hidden',
                            paddingRight: 22,
                        }}
                    >
                        <MarkdownComponent
                            source={description}
                            components={DESCRIPTION_MARKDOWN_COMPONENTS}
                            style={DESCRIPTION_MARKDOWN_STYLE}
                        />
                    </div>
                )}
            </div>
            {/* Expand affordance pinned to the module's bottom-right corner (the cell stretches full height). */}
            {descTruncated && (
                <ZoomInOutlined
                    onClick={openOverlay}
                    style={{
                        position: 'absolute',
                        bottom: 12,
                        right: 20,
                        color: antGrey.grey6,
                        fontSize: 14,
                        cursor: 'pointer',
                    }}
                />
            )}
            {expandedRect &&
                cellRect &&
                createPortal(
                    <>
                        {/* Click-away layer (stopPropagation: portal events bubble up the React tree to the card) */}
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                closeOverlay();
                            }}
                            style={{ position: 'fixed', inset: 0, zIndex: 1000 }}
                        />
                        {/* Full description, positioned over the description so it reads as the box expanding in place. */}
                        <div
                            ref={overlayRef}
                            onClick={stopCardClick}
                            style={{
                                position: 'fixed',
                                boxSizing: 'border-box',
                                top: cellRect.top - 1,
                                left: cellRect.left - 1,
                                width: cellRect.width + 2,
                                zIndex: 1001,
                                backgroundColor: runwayColors.white,
                                border: `1px solid ${antGrey.grey5}`,
                                borderRadius: '0 0 6px 6px',
                                boxShadow: 'var(--rw-shadow-md)',
                                opacity: overlayVisible ? 1 : 0,
                                transition: 'opacity 0.15s ease-in-out',
                            }}
                        >
                            <div
                                style={{
                                    maxHeight: 320,
                                    overflowY: 'auto',
                                    padding: 16,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 8,
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, minWidth: 0 }}>
                                    <div
                                        style={{
                                            width: 16,
                                            height: TITLE_LINE_HEIGHT,
                                            flexShrink: 0,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            lineHeight: 0,
                                        }}
                                    >
                                        {sourceIcon}
                                    </div>
                                    {title && (
                                        <Text
                                            style={{
                                                flex: 1,
                                                minWidth: 0,
                                                fontSize: 14,
                                                lineHeight: `${TITLE_LINE_HEIGHT}px`,
                                                fontWeight: 500,
                                                color: antGrey.grey10,
                                                wordBreak: 'break-word',
                                            }}
                                        >
                                            {title}
                                        </Text>
                                    )}
                                </div>
                                <div style={{ marginLeft: 32, paddingRight: 22 }}>
                                    <MarkdownComponent
                                        source={description}
                                        components={DESCRIPTION_MARKDOWN_COMPONENTS}
                                        style={DESCRIPTION_MARKDOWN_STYLE}
                                    />
                                </div>
                            </div>
                        </div>
                    </>,
                    document.body,
                )}
        </div>
    );
};

const ColumnHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Text style={{ ...LABEL_STYLE, fontSize: 11 }}>{children}</Text>
);

const ReporterSection: React.FC<{ issue: IssueEntity; orgId: string; dimmed?: boolean }> = ({
    issue,
    orgId,
    dimmed,
}) => {
    // issue.reporter is a userId; fall back to the source's free-form reporter string when unresolved.
    const reporter = useSelector((state: RootState) =>
        issue.reporter ? selectUserById(state, orgId, issue.appId, issue.reporter) : undefined,
    );
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 16,
                padding: '16px 8px 16px 16px',
                backgroundColor: colorWithAlpha(antGrey.grey2, 0.5),
                opacity: dimmed ? 0.6 : undefined,
            }}
        >
            <ColumnHeader>Reporter</ColumnHeader>
            <UserAvatarWithTooltip user={reporter} fallbackName={issue.reporter} />
        </div>
    );
};

export const SuggestedAvatarRing: React.FC<{
    color: string;
    suggested: boolean;
    className?: string;
    children: React.ReactNode;
}> = ({ color, suggested, className, children }) => (
    <span
        className={className}
        style={{
            display: 'inline-flex',
            borderRadius: '50%',
            padding: 2,
            background: suggested ? `repeating-conic-gradient(${color} 0deg 6deg, transparent 6deg 15deg)` : undefined,
        }}
    >
        {children}
    </span>
);

export const IssueAssigneeAvatar: React.FC<{ issue: IssueEntity; orgId: string; size?: number }> = ({
    issue,
    orgId,
    size = 28,
}) => {
    const assigneeId = issue.assigneeUserIds?.[0];
    const assigneeGroupId = issue.assigneeGroupIds?.[0];
    const assigneeAgentId = issue.assigneeAgentIds?.[0];
    const assignee = useSelector((state: RootState) =>
        assigneeId ? selectUserById(state, orgId, issue.appId, assigneeId) : undefined,
    );
    const groups = useSelector((state: RootState) => selectSortedGroups(state, orgId));
    const { agentsById } = useIssueAgents(orgId, issue.appId);
    const assignedGroup = assigneeGroupId ? groups.find((g) => g.id === assigneeGroupId) : undefined;

    const agentAvatar = assigneeAgentId && (
        <Tooltip title={agentDisplayName(assigneeAgentId, agentsById[assigneeAgentId])} placement="top">
            <Avatar size={size} style={{ backgroundColor: antGrey.grey8, fontSize: 12 }} icon={<RobotOutlined />} />
        </Tooltip>
    );
    const humanAvatar =
        assigneeId ?
            <UserAvatarWithTooltip
                user={assignee}
                fallbackName={assigneeId}
                size={size}
                bordered={issue.isAutoAssigned}
                extraTooltip={issue.isAutoAssigned ? 'Auto-assigned by Runway' : undefined}
            />
        : assigneeGroupId ?
            <Tooltip
                title={assignedGroup ? displayTextForUserChecklistGroup(assignedGroup) : assigneeGroupId}
                placement="top"
            >
                <Avatar
                    size={size}
                    style={{ backgroundColor: antMagenta.magenta5, fontSize: 12 }}
                    icon={<TeamOutlined />}
                />
            </Tooltip>
        :   null;
    if (humanAvatar || agentAvatar) {
        return humanAvatar || agentAvatar;
    }
    return (
        <Tooltip title="Unassigned" placement="top">
            <span
                style={{
                    width: size,
                    height: size,
                    boxSizing: 'border-box',
                    borderRadius: '50%',
                    border: `1.5px dashed ${antGrey.grey6}`,
                    backgroundColor: runwayColors.white,
                    display: 'inline-block',
                }}
            />
        </Tooltip>
    );
};

const AssigneeSection: React.FC<{
    issue: IssueEntity;
    orgId: string;
    preview?: boolean;
    disabled?: boolean;
    viewType: TriageViewType;
    dimmed?: boolean;
    basePath?: string;
}> = ({ issue, orgId, preview, disabled, viewType, dimmed, basePath }) => {
    const appUsers = useSelector((state: RootState) => selectAppOrgUsers(state, issue.appId));
    const groups = useSelector((state: RootState) => selectSortedGroups(state, orgId));
    const { assignableAgents } = useIssueAgents(orgId, issue.appId);
    const isAssigning = useIsLoading(TRY_UPDATE_ISSUE_ASSIGNEES, issue.id);
    const canManage = useUserCanPerformAction(
        RunwayUserAction.UAManageTriageIssues,
        undefined,
        undefined,
        undefined,
        undefined,
        issue.appId,
    );

    const selectedAssigneeKey = currentAssigneeKey(issue);
    const assignIssue = useAssignIssue(orgId, issue, viewType);

    const [assigneeSearch, setAssigneeSearch] = React.useState('');

    const menuLabel = (text: string, suggested = false) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, width: 150 }}>
            <span
                style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 15,
                    color: antGrey.grey8,
                }}
            >
                {text}
            </span>
            {suggested && (
                <img src={sparkleIcon} width={12} height={12} style={{ flexShrink: 0, marginLeft: 'auto' }} />
            )}
        </span>
    );

    const assigneeEntries = [
        ...sortUsersBySuggestedFirst(appUsers, issue.suggestedAssigneeUserIds).map((user) => {
            const name = displayUserNameLong(user);
            const isSuggested = !!issue.suggestedAssigneeUserIds?.includes(user.id);
            const avatarColor = getAvatarColorForName(name);
            return {
                key: `${ASSIGNEE_USER_PREFIX}${user.id}`,
                name,
                label: menuLabel(name, isSuggested),
                icon: (
                    <SuggestedAvatarRing color={avatarColor} suggested={isSuggested}>
                        <Avatar
                            size={28}
                            src={user.commsAvatars?.image32}
                            style={{ backgroundColor: avatarColor, fontSize: 12, border: 'none' }}
                        >
                            {name.charAt(0).toUpperCase()}
                        </Avatar>
                    </SuggestedAvatarRing>
                ),
            };
        }),
        ...groups
            .filter((group) => !group.disabled)
            .map((group) => ({
                key: `${ASSIGNEE_GROUP_PREFIX}${group.id}`,
                name: displayTextForUserChecklistGroup(group),
                label: menuLabel(displayTextForUserChecklistGroup(group)),
                icon: (
                    <SuggestedAvatarRing color="transparent" suggested={false}>
                        <Avatar
                            size={28}
                            icon={<TeamOutlined />}
                            style={{ backgroundColor: antMagenta.magenta5, fontSize: 12, border: 'none' }}
                        />
                    </SuggestedAvatarRing>
                ),
            })),
        ...assignableAgents.map((agent) => ({
            key: `${ASSIGNEE_AGENT_PREFIX}${agent.id}`,
            name: agent.name,
            label: menuLabel(agent.name),
            icon: (
                <SuggestedAvatarRing color="transparent" suggested={false}>
                    <Avatar
                        size={28}
                        icon={<RobotOutlined />}
                        style={{ backgroundColor: antGrey.grey8, fontSize: 12, border: 'none' }}
                    />
                </SuggestedAvatarRing>
            ),
        })),
    ];

    const assigneeMenuItems = assigneeEntries
        .filter(({ name }) => name.toLowerCase().includes(assigneeSearch.trim().toLowerCase()))
        .map(({ key, label, icon }) => ({ key, label, icon }));

    return (
        <div
            onClick={stopCardClick}
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 16,
                padding: '16px 8px',
                backgroundColor: antGrey.grey2,
                opacity: dimmed ? 0.6 : undefined,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <ColumnHeader>Assignee</ColumnHeader>
                {issue.isAutoAssigned && (
                    <Tooltip
                        placement="top"
                        overlayStyle={hasAutoAssignmentReasons(issue) ? { maxWidth: 360 } : undefined}
                        title={
                            hasAutoAssignmentReasons(issue) ?
                                <AutoAssignmentReasons issue={issue} basePath={basePath} />
                            :   'Auto-assigned by Runway'
                        }
                    >
                        <img src={sparkleIcon} width={12} height={12} />
                    </Tooltip>
                )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                <IssueAssigneeAvatar issue={issue} orgId={orgId} />
                {preview ?
                    null
                : isAssigning ?
                    <LoadingOutlined spin style={{ color: antGrey.grey6, fontSize: 18 }} />
                :   <Dropdown
                        trigger={['click']}
                        placement="bottomRight"
                        disabled={!canManage || disabled}
                        onOpenChange={(open) => {
                            if (!open) setAssigneeSearch('');
                        }}
                        menu={{
                            items: assigneeMenuItems,
                            selectedKeys: selectedAssigneeKey ? [selectedAssigneeKey] : [],
                            onClick: ({ key }) => assignIssue(key),
                            style: { maxHeight: 320, overflowY: 'auto' },
                        }}
                        dropdownRender={(menu) => (
                            <div
                                style={{
                                    backgroundColor: antGrey.grey1,
                                    borderRadius: 8,
                                    border: `1px solid ${antGrey.grey5}`,
                                    boxShadow: `0 6px 16px 0 ${colorWithAlpha(antGrey.grey8, 0.12)}`,
                                    overflow: 'hidden',
                                }}
                            >
                                <div style={{ padding: 8 }}>
                                    <Input
                                        autoFocus
                                        allowClear
                                        size="small"
                                        placeholder="Search..."
                                        prefix={<SearchOutlined style={{ color: antGrey.grey6 }} />}
                                        value={assigneeSearch}
                                        onChange={(e) => setAssigneeSearch(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => e.stopPropagation()}
                                    />
                                </div>
                                {assigneeMenuItems.length > 0 ?
                                    menu
                                :   <div
                                        style={{
                                            padding: '8px 12px 12px',
                                            color: antGrey.grey6,
                                            fontSize: 13,
                                        }}
                                    >
                                        No matches
                                    </div>
                                }
                            </div>
                        )}
                    >
                        <Tooltip title={canManage ? undefined : DISALLOWED_ACTION_ERROR_MESSAGE}>
                            <CaretDownOutlined
                                style={{
                                    color: antGrey.grey5,
                                    fontSize: 22,
                                    cursor: canManage && !disabled ? 'pointer' : 'not-allowed',
                                }}
                            />
                        </Tooltip>
                    </Dropdown>
                }
            </div>
        </div>
    );
};

const BuildConnector: React.FC = () => {
    const dot = <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: antGrey.grey5 }} />;
    return (
        <div
            style={{
                position: 'absolute',
                left: -20,
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            {dot}
            <span style={{ height: 20, width: 1, backgroundColor: antGrey.grey5 }} />
            {dot}
        </div>
    );
};

const MAX_BUILD_LABEL_CHARS = 7;

const truncateMiddle = (value: string, maxChars: number) => {
    if (value.length <= maxChars) {
        return value;
    }
    const head = Math.ceil((maxChars - 1) / 2);
    const tail = maxChars - 1 - head;
    return `${value.slice(0, head)}…${value.slice(value.length - tail)}`;
};

const BuildRow: React.FC<{
    value?: string;
    url?: string;
    integrationId?: string;
    isProcessed?: boolean;
    dashedBottom?: boolean;
}> = ({ value, url, integrationId, isProcessed = true, dashedBottom }) => {
    const label = value ? `#${truncateMiddle(value, MAX_BUILD_LABEL_CHARS)}` : '--';
    const isTruncated = !!value && label !== `#${value}`;
    const tooltip =
        !value ? undefined
        : !isProcessed ? `#${value} (processing)`
        : isTruncated ? `#${value}`
        : undefined;
    return (
        <Tooltip title={tooltip}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '4px 10px',
                    borderBottom: dashedBottom ? `1px dashed ${antGrey.grey5}` : undefined,
                    cursor: 'default',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                }}
            >
                {value && integrationId && (
                    <IntegrationIcon integrationId={integrationId as IntegrationType} size={18} />
                )}
                {value && url ?
                    <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: rwColors.primary, fontSize: 13, fontWeight: 500 }}
                    >
                        {label}
                    </a>
                :   <Text style={{ color: value && isProcessed ? antGrey.grey9 : antGrey.grey6, fontSize: 12 }}>
                        {label}
                    </Text>
                }
            </div>
        </Tooltip>
    );
};

const VersionColumn: React.FC<{ issue: IssueEntity; orgId: string; dimmed?: boolean; disabled?: boolean }> = ({
    issue,
    orgId,
    dimmed,
    disabled,
}) => {
    const dispatch = useDispatch();
    const version = issue.versions?.[0];
    const build = issue.build;
    const ciIdentifier = build?.identifier;
    const releases = useSelector((state: RootState) => selectReleasesForAppId(state, issue.appId));
    const isUpdating = useIsLoading(TRY_UPDATE_ISSUE, issue.id);
    const canManage = useUserCanPerformAction(
        RunwayUserAction.UAManageTriageIssues,
        undefined,
        undefined,
        undefined,
        undefined,
        issue.appId,
    );

    const versionOptions = releases.map((release) => ({
        value: release.version,
        label: release.displayableVersion || release.version,
    }));

    const selectVersion = (newVersion: string) =>
        dispatch(tryUpdateIssue({ orgId, appId: issue.appId, issueId: issue.id, version: newVersion }));

    return (
        <div
            onClick={stopCardClick}
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 6,
                padding: '12px 0 16px 16px',
                backgroundColor: antGrey.grey3,
                opacity: dimmed ? 0.6 : undefined,
            }}
        >
            <div style={{ position: 'relative', width: 106 }}>
                {/* Flag aligned to the build connector's left offset for a consistent left rail. */}
                <div
                    style={{
                        position: 'absolute',
                        left: -20,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 7,
                    }}
                >
                    <Tooltip title="First detected version" placement="top">
                        <FlagOutlined style={{ color: antGrey.grey7, fontSize: 13 }} />
                    </Tooltip>
                </div>
                <Tooltip title={canManage ? undefined : DISALLOWED_ACTION_ERROR_MESSAGE}>
                    <Select
                        size="small"
                        showSearch
                        optionFilterProp="label"
                        value={version}
                        options={versionOptions}
                        disabled={!canManage || disabled}
                        placeholder="--"
                        style={{ width: 106 }}
                        popupMatchSelectWidth={false}
                        labelRender={({ label, value }) => (
                            <span style={{ fontWeight: 600, fontSize: 12, color: antGrey.grey8 }}>
                                {label ?? value}
                            </span>
                        )}
                        suffixIcon={
                            isUpdating ?
                                <LoadingOutlined
                                    spin
                                    style={{ color: antGrey.grey6, fontSize: 16, pointerEvents: 'none' }}
                                />
                            :   <CaretDownOutlined
                                    style={{ color: antGrey.grey6, fontSize: 16, pointerEvents: 'none' }}
                                />
                        }
                        onChange={(value) => selectVersion(value)}
                    />
                </Tooltip>
            </div>
            {/* Build box — top: CI build (link + integration); bottom: matching App Store build. Dashed when empty. */}
            <div style={{ position: 'relative', width: 106 }}>
                <BuildConnector />
                <div
                    style={{
                        backgroundColor: runwayColors.white,
                        border: `1px ${ciIdentifier ? 'solid' : 'dashed'} ${antGrey.grey5}`,
                        borderRadius: 6,
                        overflow: 'hidden',
                    }}
                >
                    <BuildRow value={ciIdentifier} url={build?.url} integrationId={build?.integrationId} dashedBottom />
                    <BuildRow
                        value={build?.appStoreBuild}
                        integrationId={build?.appStoreIntegrationId}
                        isProcessed={build?.appStoreBuildIsProcessed}
                    />
                </div>
            </div>
        </div>
    );
};

// Menu key for "Request approval for a fix" — not a transition (opens a modal, wired in a later PR).
const REQUEST_FIX_KEY = 'requestFix';
const ADD_SOURCE_KEY = 'addSource';
const UNMERGE_SOURCE_KEY = 'unmergeSource';
const MARK_RELATED_KEY = 'markRelated';

const ActionsColumn: React.FC<{
    issue: IssueEntity;
    orgId: string;
    viewType: TriageViewType;
    disabled?: boolean;
}> = ({ issue, orgId, viewType, disabled }) => {
    const dispatch = useDispatch();
    const isAssigned =
        (issue.assigneeUserIds?.length ?? 0) +
            (issue.assigneeGroupIds?.length ?? 0) +
            (issue.assigneeAgentIds?.length ?? 0) >
        0;
    const isClosed = CLOSED_ISSUE_STATES.includes(issue.state);
    // Whether a button (reopen / not-a-priority / request-fix) sits above the overflow "..." menu.
    const hasTopAction = isClosed || !isAssigned || !issue.fixRequest;
    const isTransitioning = useIsLoading(TRY_TRANSITION_ISSUE, issue.id);
    const isReopening = useIsLoading(TRY_REOPEN_ISSUE, issue.id);
    const canApproveRejectRole = useUserCanPerformAction(
        RunwayUserAction.UAUpdateFixRequestStatus,
        undefined,
        undefined,
        undefined,
        undefined,
        issue.appId,
    );
    const isRequiredApprover = useIsRequiredFixApprover(orgId, issue.appId);
    const canApproveReject = canApproveRejectRole && isRequiredApprover;
    const canManage = useUserCanPerformAction(
        RunwayUserAction.UAManageTriageIssues,
        undefined,
        undefined,
        undefined,
        undefined,
        issue.appId,
    );
    const requiresFixApprovals = useSelector(selectRequiresFixRequestsApprovals(issue.appId));
    const [requestFixOpen, setRequestFixOpen] = React.useState(false);
    const [addSourceOpen, setAddSourceOpen] = React.useState(false);
    const [markRelatedOpen, setMarkRelatedOpen] = React.useState(false);
    const [unmergeSourceOpen, setUnmergeSourceOpen] = React.useState(false);

    const transition = (targetState: IssueState) =>
        dispatch(tryTransitionIssue({ orgId, appId: issue.appId, issueId: issue.id, targetState, viewType }));
    const reopen = () => dispatch(tryReopenIssue({ orgId, appId: issue.appId, issueId: issue.id, viewType }));

    const menuLabel = (text: string) => <span style={{ color: antGrey.grey8 }}>{text}</span>;
    // Wraps a menu label in the no-permission tooltip when the gating permission is missing.
    const gatedLabel = (text: string, allowed: boolean) =>
        allowed ?
            menuLabel(text)
        :   <Tooltip title={DISALLOWED_ACTION_ERROR_MESSAGE} placement="left">
                {menuLabel(text)}
            </Tooltip>;
    let fixMenuItems;
    if (!issue.fixRequest) {
        fixMenuItems = [
            {
                key: REQUEST_FIX_KEY,
                disabled: !canManage,
                icon: <ToolOutlined style={{ color: antGrey.grey7 }} />,
                label: gatedLabel(requestFixActionLabel(requiresFixApprovals), canManage),
            },
        ];
    } else if (issue.state === IssueState.FixRequested && requiresFixApprovals) {
        fixMenuItems = [
            {
                key: IssueState.FixApproved,
                disabled: !canApproveReject,
                icon: (
                    <CheckCircleOutlined style={{ color: canApproveReject ? rwColors.statusGreen : antGrey.grey6 }} />
                ),
                label: gatedLabel('Approve fix', canApproveReject),
            },
            {
                key: IssueState.FixRejected,
                disabled: !canApproveReject,
                icon: <CloseCircleOutlined style={{ color: canApproveReject ? rwColors.statusRed : antGrey.grey6 }} />,
                label: gatedLabel('Reject fix', canApproveReject),
            },
        ];
    } else {
        fixMenuItems = [];
    }
    const menuItems =
        isClosed ?
            []
        :   [
                ...fixMenuItems,
                {
                    key: IssueState.NotAPriority,
                    disabled: !canManage,
                    icon: <CloseCircleOutlined style={{ color: antGrey.grey7 }} />,
                    label: gatedLabel("Mark as 'Not a priority'", canManage),
                },
                ...(issue.state === IssueState.FixApproved ?
                    [
                        {
                            key: IssueState.FixMerged,
                            disabled: !canManage,
                            icon: <CheckCircleOutlined style={{ color: antGrey.grey7 }} />,
                            label: gatedLabel("Mark as 'Resolved'", canManage),
                        },
                    ]
                :   []),
                { type: 'divider' as const },
                {
                    key: MARK_RELATED_KEY,
                    disabled: !canManage,
                    icon: <LinkOutlined style={{ color: antGrey.grey7 }} />,
                    label: gatedLabel('Mark as related...', canManage),
                },
                { type: 'divider' as const },
                {
                    key: ADD_SOURCE_KEY,
                    disabled: !canManage,
                    icon: <PlusCircleOutlined style={{ color: antGrey.grey7 }} />,
                    label: gatedLabel('Pull in existing signal...', canManage),
                },
                ...(issue.sources.length > 1 ?
                    [
                        {
                            key: UNMERGE_SOURCE_KEY,
                            disabled: !canManage,
                            icon: <MinusCircleOutlined style={{ color: antGrey.grey7 }} />,
                            label: gatedLabel('Unmerge signal...', canManage),
                        },
                    ]
                :   []),
            ];
    const hasStackedActions = hasTopAction && menuItems.length > 0;

    const onMenuClick = ({ key }: { key: string }) => {
        if (key === REQUEST_FIX_KEY) {
            setRequestFixOpen(true);
            return;
        }
        if (key === ADD_SOURCE_KEY) {
            setAddSourceOpen(true);
            return;
        }
        if (key === UNMERGE_SOURCE_KEY) {
            setUnmergeSourceOpen(true);
            return;
        }
        if (key === MARK_RELATED_KEY) {
            setMarkRelatedOpen(true);
            return;
        }
        // Every other key is the target IssueState.
        transition(key as IssueState);
    };

    return (
        <>
            <div
                onClick={stopCardClick}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    justifyContent: hasStackedActions ? 'flex-start' : 'center',
                    gap: 20,
                    padding: hasStackedActions ? '30px 10px 16px 0' : '16px 10px 16px 0',
                    backgroundColor: antGrey.grey3,
                }}
            >
                {isClosed ?
                    <Tooltip title={canManage ? 'Reopen' : DISALLOWED_ACTION_ERROR_MESSAGE} placement="top">
                        <Button
                            size="small"
                            shape="circle"
                            loading={isReopening}
                            disabled={!canManage || disabled}
                            onClick={reopen}
                            icon={
                                isReopening ? undefined : (
                                    <UndoOutlined style={{ color: antGrey.grey6, fontSize: 11 }} />
                                )
                            }
                            style={{
                                width: 20,
                                height: 20,
                                minWidth: 20,
                                borderRadius: 16,
                                borderWidth: 1.5,
                                backgroundColor: runwayColors.white,
                                borderColor: antGrey.grey6,
                                boxShadow: `0 0 0 2px ${runwayColors.white}`,
                            }}
                        />
                    </Tooltip>
                : !isAssigned ?
                    <Tooltip
                        title={canManage ? "Mark as 'Not a priority'" : DISALLOWED_ACTION_ERROR_MESSAGE}
                        placement="top"
                    >
                        <Button
                            size="small"
                            type="text"
                            shape="circle"
                            loading={isTransitioning}
                            disabled={!canManage || disabled}
                            onClick={() => transition(IssueState.NotAPriority)}
                            icon={
                                isTransitioning ? undefined : (
                                    <CloseCircleOutlined style={{ color: antGrey.grey6, fontSize: 20 }} />
                                )
                            }
                            style={{
                                width: 20,
                                height: 20,
                                minWidth: 20,
                                backgroundColor: runwayColors.white,
                                boxShadow: `0 0 0 2px ${runwayColors.white}`,
                            }}
                        />
                    </Tooltip>
                : !issue.fixRequest ?
                    <Tooltip
                        title={
                            canManage ? requestFixActionLabel(requiresFixApprovals) : DISALLOWED_ACTION_ERROR_MESSAGE
                        }
                        placement="top"
                    >
                        <Button
                            size="small"
                            shape="circle"
                            disabled={!canManage || disabled}
                            onClick={() => setRequestFixOpen(true)}
                            icon={<ToolFilled style={{ color: antGrey.grey6, fontSize: 11 }} />}
                            style={{
                                width: 20,
                                height: 20,
                                minWidth: 20,
                                borderRadius: 16,
                                backgroundColor: runwayColors.white,
                                borderColor: antGrey.grey6,
                                borderWidth: 1.5,
                                boxShadow: `0 0 0 2px ${runwayColors.white}`,
                            }}
                        />
                    </Tooltip>
                :   null}
                {menuItems.length > 0 && (
                    <Dropdown
                        trigger={['click']}
                        placement="bottomRight"
                        disabled={disabled}
                        menu={{ items: menuItems, onClick: onMenuClick }}
                    >
                        <Button
                            size="small"
                            type="text"
                            loading={isTransitioning}
                            disabled={disabled}
                            icon={isTransitioning ? undefined : <EllipsisOutlined style={{ color: antGrey.grey7 }} />}
                            style={{ width: 20, height: 20, minWidth: 20, padding: 0 }}
                        />
                    </Dropdown>
                )}
            </div>
            <div onClick={stopCardClick}>
                <RequestFixModal
                    open={requestFixOpen}
                    onClose={() => setRequestFixOpen(false)}
                    orgId={orgId}
                    appId={issue.appId}
                    issue={issue}
                    viewType={viewType}
                />
                <AddSourceModal
                    open={addSourceOpen}
                    onClose={() => setAddSourceOpen(false)}
                    orgId={orgId}
                    appId={issue.appId}
                    issueId={issue.id}
                />
                <UnmergeSourceModal
                    open={unmergeSourceOpen}
                    onClose={() => setUnmergeSourceOpen(false)}
                    orgId={orgId}
                    issue={issue}
                />
                <MarkRelatedModal
                    open={markRelatedOpen}
                    onClose={() => setMarkRelatedOpen(false)}
                    orgId={orgId}
                    appId={issue.appId}
                    issue={issue}
                />
            </div>
        </>
    );
};
